import { AuthError, register } from "../services/auth";

const template = document.createElement("template");
template.innerHTML = `
	<style>
		:host {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;
			padding: var(--space-lg);
		}

		.container {
			width: 100%;
			max-width: 320px;
			text-align: center;
		}

		h1 {
			font-size: 2rem;
			font-weight: 600;
			margin-bottom: var(--space-sm);
		}

		.subtitle {
			color: var(--color-text-muted);
			margin-bottom: var(--space-lg);
		}

		.form {
			display: flex;
			flex-direction: column;
			gap: var(--space-md);
		}

		.input-group {
			display: flex;
			flex-direction: column;
			gap: var(--space-xs);
			text-align: left;
		}

		label {
			font-size: 0.875rem;
			color: var(--color-text-muted);
		}

		input {
			width: 100%;
			padding: var(--space-sm) var(--space-md);
			border: 1px solid var(--color-primary);
			border-radius: var(--radius-sm);
			background: var(--color-surface);
			color: var(--color-text);
			font-size: 1rem;
		}

		input:focus {
			outline: none;
			border-color: var(--color-accent);
		}

		input::placeholder {
			color: var(--color-text-muted);
		}

		button {
			width: 100%;
			padding: var(--space-md);
			font-size: 1rem;
			font-weight: 500;
		}

		button:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		.error {
			color: var(--color-accent);
			font-size: 0.875rem;
			min-height: 1.25rem;
		}

		.hint {
			font-size: 0.75rem;
			color: var(--color-text-muted);
		}
	</style>
	<div class="container">
		<h1>WAYF</h1>
		<p class="subtitle">Find your friends</p>
		<form class="form">
			<div class="input-group">
				<label for="nickname">Choose a nickname</label>
				<input
					type="text"
					id="nickname"
					name="nickname"
					placeholder="Your name"
					maxlength="50"
					autocomplete="off"
					required
				/>
				<span class="hint">1-50 characters, visible to your friends</span>
			</div>
			<p class="error"></p>
			<button type="submit">Get Started</button>
		</form>
	</div>
`;

class OnboardingFlow extends HTMLElement {
	private form!: HTMLFormElement;
	private input!: HTMLInputElement;
	private button!: HTMLButtonElement;
	private errorEl!: HTMLElement;

	constructor() {
		super();
		this.attachShadow({ mode: "open" });
		this.shadowRoot!.appendChild(template.content.cloneNode(true));
	}

	connectedCallback() {
		this.form = this.shadowRoot!.querySelector(".form")!;
		this.input = this.shadowRoot!.querySelector("#nickname")!;
		this.button = this.shadowRoot!.querySelector("button")!;
		this.errorEl = this.shadowRoot!.querySelector(".error")!;

		this.form.addEventListener("submit", this.handleSubmit.bind(this));
		this.input.addEventListener("input", this.clearError.bind(this));
	}

	private clearError() {
		this.errorEl.textContent = "";
	}

	private setLoading(loading: boolean) {
		this.button.disabled = loading;
		this.input.disabled = loading;
		this.button.textContent = loading ? "Creating account..." : "Get Started";
	}

	private async handleSubmit(event: Event) {
		event.preventDefault();

		const nickname = this.input.value.trim();
		if (!nickname) {
			this.errorEl.textContent = "Please enter a nickname";
			return;
		}

		if (nickname.length > 50) {
			this.errorEl.textContent = "Nickname must be 50 characters or less";
			return;
		}

		this.setLoading(true);
		this.clearError();

		try {
			await register(nickname);
			this.dispatchEvent(new CustomEvent("register-complete", {
				bubbles: true,
				composed: true
			}));
		} catch (error) {
			if (error instanceof AuthError) {
				switch (error.code) {
					case "NETWORK_ERROR":
						this.errorEl.textContent = "Network error. Please check your connection.";
						break;
					case "USER_EXISTS":
						this.errorEl.textContent = "Registration failed. Please try again.";
						break;
					default:
						this.errorEl.textContent = error.message;
				}
			} else {
				this.errorEl.textContent = "An unexpected error occurred";
			}
			this.setLoading(false);
		}
	}
}

customElements.define("onboarding-flow", OnboardingFlow);
